import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Bot,
  Clock3,
  MessageCircleMore,
  RefreshCw,
  Search,
  TriangleAlert,
  ThumbsDown,
  ThumbsUp,
  Wrench
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  fetchDashboardInsights,
  type DashboardInsights,
  type DashboardSummary
} from '@/api/admin';

const PERIODS = [7, 30, 90] as const;

const TOOL_NAMES: Record<string, string> = {
  'Searching the IPMA website': 'IPMA website search',
  'Preparing IPMA page guidance': 'Page guidance',
  ipma_site_search: 'IPMA website search',
  ipma_navigation: 'Page guidance'
};

const formatResponseTime = (seconds: number) => {
  if (!seconds) return '—';
  return seconds < 1 ? '< 1s' : `${seconds.toFixed(1)}s`;
};

const formatToolName = (name: string) =>
  TOOL_NAMES[name] || name.replace(/[_-]+/g, ' ');

function MetricCard({
  label,
  value,
  description,
  Icon
}: {
  label: string;
  value: string | number;
  description: string;
  Icon: typeof MessageCircleMore;
}) {
  return (
    <div className="min-w-[10rem] flex-1 rounded-xl border border-border/70 bg-card p-4 shadow-[0_1px_2px_rgba(15,23,42,0.03)]">
      <div className="flex items-center justify-between gap-3 text-xs font-medium text-muted-foreground">
        <span>{label}</span>
        <span className="flex size-7 items-center justify-center rounded-lg bg-primary/8 text-primary">
          <Icon className="size-3.5 stroke-[1.8]" />
        </span>
      </div>
      <p className="mt-3 text-2xl font-bold tracking-[-0.04em] text-foreground">
        {value}
      </p>
      <p className="mt-1 text-xs text-muted-foreground">{description}</p>
    </div>
  );
}

function LoadingOverview() {
  return (
    <div className="space-y-5 animate-pulse" aria-label="Loading dashboard data">
      <div className="h-9 w-56 rounded-lg bg-muted" />
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {[0, 1, 2, 3].map((index) => (
          <div key={index} className="h-32 rounded-xl bg-muted" />
        ))}
      </div>
      <div className="grid gap-4 lg:grid-cols-[1.45fr_0.9fr]">
        <div className="h-72 rounded-xl bg-muted" />
        <div className="h-72 rounded-xl bg-muted" />
      </div>
    </div>
  );
}

export default function DailyStatsTab() {
  const [period, setPeriod] = useState<(typeof PERIODS)[number]>(30);
  const [insights, setInsights] = useState<DashboardInsights | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      setInsights(await fetchDashboardInsights(period));
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    void load();
  }, [load]);

  const maxQuestions = useMemo(
    () => Math.max(...(insights?.daily.map((day) => day.questions) || []), 1),
    [insights]
  );
  const maxToolCount = useMemo(
    () => Math.max(...(insights?.tools.map((tool) => tool.count) || []), 1),
    [insights]
  );
  const summary: DashboardSummary | undefined = insights?.summary;
  const totalFeedback =
    (summary?.positive_feedback || 0) + (summary?.negative_feedback || 0);
  const positiveRate = totalFeedback
    ? Math.round(((summary?.positive_feedback || 0) / totalFeedback) * 100)
    : 0;
  const unanswered = insights?.unanswered ?? [];

  if (loading && !insights) return <LoadingOverview />;

  if (error || !insights || !summary) {
    return (
      <div className="flex min-h-64 flex-col items-center justify-center rounded-xl border border-dashed border-border px-5 text-center">
        <p className="text-sm font-medium">Could not load Copilot insights</p>
        <p className="mt-1 text-sm text-muted-foreground">
          The dashboard data is still available; please try again.
        </p>
        <Button
          className="mt-4"
          variant="outline"
          size="sm"
          onClick={() => void load()}
        >
          <RefreshCw className="mr-2 size-3.5" />
          Try again
        </Button>
      </div>
    );
  }

  return (
    <section className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-sm font-semibold tracking-[-0.02em]">
            Copilot overview
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            Real usage from the last {period} days.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-border/80 bg-muted/35 p-0.5">
            {PERIODS.map((days) => (
              <button
                key={days}
                type="button"
                onClick={() => setPeriod(days)}
                className={cn(
                  'rounded-md px-2.5 py-1 text-xs font-semibold transition-colors duration-150',
                  period === days
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                {days}d
              </button>
            ))}
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="size-8 text-muted-foreground hover:text-foreground"
            onClick={() => void load()}
            disabled={loading}
            aria-label="Refresh dashboard data"
          >
            <RefreshCw className={cn('size-3.5', loading && 'animate-spin')} />
          </Button>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          label="Conversations"
          value={summary.conversations}
          description="New conversations started"
          Icon={MessageCircleMore}
        />
        <MetricCard
          label="Questions"
          value={summary.questions}
          description={`${summary.answers} assistant answers`}
          Icon={Bot}
        />
        <MetricCard
          label="Tools used"
          value={summary.tool_calls}
          description={
            summary.tool_errors
              ? `${summary.tool_errors} completed with an error`
              : 'No tool errors recorded'
          }
          Icon={Wrench}
        />
        <MetricCard
          label="Avg. response"
          value={formatResponseTime(summary.average_response_seconds)}
          description="Assistant response time"
          Icon={Clock3}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-[1.45fr_0.9fr]">
        <div className="rounded-xl border border-border/70 bg-card p-4 shadow-[0_1px_2px_rgba(15,23,42,0.03)]">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-sm font-semibold">Question volume</h2>
              <p className="mt-1 text-xs text-muted-foreground">
                Questions asked per active day
              </p>
            </div>
            <span className="text-xs font-medium text-muted-foreground">
              {summary.questions} total
            </span>
          </div>

          {insights.daily.length ? (
            <div
              className="mt-6 flex h-44 items-end gap-1.5"
              aria-label="Daily question volume"
            >
              {insights.daily.map((day) => {
                const height = Math.max(
                  5,
                  Math.round((day.questions / maxQuestions) * 100)
                );
                const label = new Date(
                  `${day.date}T12:00:00`
                ).toLocaleDateString(undefined, {
                  month: 'short',
                  day: 'numeric'
                });
                return (
                  <div
                    key={day.date}
                    className="group flex h-full min-w-0 flex-1 flex-col justify-end"
                    title={`${label}: ${day.questions} questions, ${day.answers} answers`}
                  >
                    <div className="relative flex flex-1 items-end">
                      <div
                        className="w-full rounded-t-sm bg-primary/80 transition-colors duration-150 group-hover:bg-primary"
                        style={{ height: `${height}%` }}
                      />
                    </div>
                    <span className="mt-2 truncate text-center text-[10px] text-muted-foreground">
                      {label}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex h-44 items-center justify-center text-sm text-muted-foreground">
              No questions in this period.
            </div>
          )}
        </div>

        <div className="rounded-xl border border-border/70 bg-card p-4 shadow-[0_1px_2px_rgba(15,23,42,0.03)]">
          <h2 className="text-sm font-semibold">Feedback signal</h2>
          {totalFeedback ? (
            <>
              <div className="mt-5 flex items-end gap-2">
                <p className="text-3xl font-bold tracking-[-0.04em]">
                  {positiveRate}%
                </p>
                <p className="pb-1 text-xs text-muted-foreground">positive</p>
              </div>
              <div className="mt-3 flex h-2 overflow-hidden rounded-full bg-muted">
                <span
                  className="bg-emerald-500"
                  style={{ width: `${positiveRate}%` }}
                />
                <span
                  className="bg-destructive/75"
                  style={{ width: `${100 - positiveRate}%` }}
                />
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <ThumbsUp className="size-3.5 text-emerald-600" />
                  {summary.positive_feedback} positive
                </div>
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <ThumbsDown className="size-3.5 text-destructive" />
                  {summary.negative_feedback} negative
                </div>
              </div>
            </>
          ) : (
            <div className="flex h-32 items-center text-sm text-muted-foreground">
              No feedback received in this period.
            </div>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-border/70 bg-card p-4 shadow-[0_1px_2px_rgba(15,23,42,0.03)]">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-sm font-semibold">Tool activity</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Actual tools used while answering users.
            </p>
          </div>
          <Search className="mt-0.5 size-4 text-muted-foreground" />
        </div>

        {insights.tools.length ? (
          <div className="mt-5 space-y-3.5">
            {insights.tools.map((tool) => (
              <div key={tool.name}>
                <div className="flex items-center justify-between gap-4 text-xs">
                  <span className="min-w-0 truncate font-medium text-foreground">
                    {formatToolName(tool.name)}
                  </span>
                  <span className="shrink-0 text-muted-foreground">
                    {tool.count} use{tool.count === 1 ? '' : 's'}
                    {tool.errors ? ` · ${tool.errors} errors` : ''}
                  </span>
                </div>
                <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-muted">
                  <div
                    className={cn(
                      'h-full rounded-full',
                      tool.errors ? 'bg-amber-500' : 'bg-primary'
                    )}
                    style={{
                      width: `${Math.max(4, (tool.count / maxToolCount) * 100)}%`
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex h-24 items-center text-sm text-muted-foreground">
            No tool activity in this period.
          </div>
        )}
      </div>

      <div className="rounded-xl border border-border/70 bg-card p-4 shadow-[0_1px_2px_rgba(15,23,42,0.03)]">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-sm font-semibold">Needs attention</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Recent questions that did not receive an assistant response.
            </p>
          </div>
          <TriangleAlert className="mt-0.5 size-4 text-amber-600" />
        </div>

        {unanswered.length ? (
          <div className="mt-4 divide-y divide-border/60">
            {unanswered.map((item) => (
              <div
                className="flex items-start justify-between gap-4 py-3 first:pt-0 last:pb-0"
                key={`${item.question}-${item.latest_at}`}
              >
                <p className="min-w-0 text-sm leading-5 text-foreground">
                  {item.question}
                </p>
                <span className="shrink-0 rounded-md bg-amber-500/10 px-2 py-1 text-[11px] font-semibold text-amber-700 dark:text-amber-400">
                  {item.occurrences}x
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex h-24 items-center text-sm text-muted-foreground">
            Every question in this period received a response.
          </div>
        )}
      </div>
    </section>
  );
}
